/**
 * Everything that arrived by email, newest first.
 *
 * The page answers "what happened to the receipt I forwarded?" file by file: became a document,
 * was attached to one (the receipt next to its invoice), was already in the books, or was left out
 * and why. Emails from unknown senders wait here until a member imports or discards them.
 */
import { Check, Mail, ShieldAlert, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { BelegNumber, KindBadge } from "@/components/documents/document-badges";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useApproveMessage,
  useDiscardMessage,
  useInboundMessages,
  useKnownFiles,
} from "@/hooks/use-inbox";
import { inboxAddress, messageFiles, type FileOutcome, type InboundMessage } from "@/lib/api/inbox";
import { formatDate } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

type Filter = "all" | "quarantined" | "imported" | "other";

/** Reason codes services/inbox writes; anything else (an error message) is shown as is. */
const REASONS = [
  "sender_not_allowed",
  "no_receipts",
  "already_in_books",
  "receipt_of_invoice",
  "same_transaction",
  "same_transaction_as_existing",
  "page_of_receipt",
  "too_small",
  "identical_file",
  "unsupported_type",
  "not_a_receipt_name",
  "too_many",
  "no_amount",
  "unreadable_image",
  "xml_without_invoice",
] as const;
type Reason = (typeof REASONS)[number];
const isReason = (value: string): value is Reason => (REASONS as readonly string[]).includes(value);

const STATUS_CLASS: Record<string, string> = {
  imported: "text-success",
  quarantined: "text-warning",
  failed: "text-destructive",
  processing: "text-muted-foreground",
  empty: "text-muted-foreground",
  discarded: "text-muted-foreground",
};

const OUTCOME_CLASS: Record<FileOutcome["outcome"], string> = {
  document: "text-success",
  attached: "text-success",
  duplicate: "text-muted-foreground",
  not_a_receipt: "text-muted-foreground",
  skipped: "text-muted-foreground",
  pending: "text-muted-foreground",
};

function matches(message: InboundMessage, filter: Filter): boolean {
  if (filter === "all") return true;
  if (filter === "quarantined")
    return message.status === "quarantined" || message.status === "failed";
  if (filter === "imported") return message.status === "imported";
  return !["quarantined", "failed", "imported"].includes(message.status);
}

export function MailboxPage() {
  const { t } = useTranslation();
  const reasonText = (reason: string) =>
    isReason(reason) ? t(`mailbox.reason.${reason}`) : reason;
  const { workspace, canWrite } = useWorkspace();
  const { data: messages, isPending } = useInboundMessages(workspace?.id);
  const approve = useApproveMessage();
  const discard = useDiscardMessage();
  const [filter, setFilter] = useState<Filter>("all");

  const waiting = (messages ?? []).filter((message) => matches(message, "quarantined")).length;
  // A waiting email's files that are already in the books, said before anyone imports it.
  const { data: known } = useKnownFiles(
    workspace?.id,
    (messages ?? [])
      .filter((message) => matches(message, "quarantined"))
      .flatMap((message) => messageFiles(message).map((file) => file.sha256)),
  );
  const visible = (messages ?? []).filter((message) => matches(message, filter));

  async function runApprove(message: InboundMessage, allowSender: boolean) {
    try {
      const result = await approve.mutateAsync({ id: message.id, allowSender });
      toast.success(result.status === "imported" ? t("mailbox.imported") : t("mailbox.processed"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("mailbox.importFailed"));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("mailbox.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {workspace?.inboxToken
              ? t("mailbox.subtitle", {
                  address: inboxAddress("ER", workspace.inboxToken, workspace.name),
                })
              : t("mailbox.subtitleNoAddress")}
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/settings?tab=inbox">{t("mailbox.addresses")}</Link>
        </Button>
      </div>

      <Tabs value={filter} onValueChange={(value) => setFilter(value as Filter)}>
        <TabsList>
          <TabsTrigger value="all">{t("common.all")}</TabsTrigger>
          <TabsTrigger value="quarantined" data-testid="filter-quarantined">
            {t("mailbox.filter.quarantined")}
            {waiting > 0 && <span className="ml-1.5 text-warning num">{waiting}</span>}
          </TabsTrigger>
          <TabsTrigger value="imported">{t("mailbox.filter.imported")}</TabsTrigger>
          <TabsTrigger value="other">{t("mailbox.filter.other")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {isPending && <Skeleton className="h-40 w-full" aria-busy="true" />}

      {messages && visible.length === 0 && (
        <EmptyState
          title={filter === "all" ? t("mailbox.emptyTitle") : t("mailbox.nothingHere")}
          description={filter === "all" ? t("mailbox.emptyText") : undefined}
          testId="mailbox-empty"
        />
      )}

      <ul className="space-y-3">
        {visible.map((message) => {
          const files = messageFiles(message);
          const held = message.status === "quarantined" || message.status === "failed";
          return (
            <li
              key={message.id}
              className={cn("rounded-lg border p-4", held && "border-warning/50")}
              data-testid="inbound-message"
              data-status={message.status}
            >
              <div className="flex flex-wrap items-start gap-3">
                {held ? (
                  <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" />
                ) : (
                  <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="truncate font-medium">
                    {message.subject || t("mailbox.noSubject")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {message.senderName ? `${message.senderName} · ` : ""}
                    {message.sender} · {formatDate(message.receivedAt || message.created)}
                  </p>
                </div>
                <KindBadge kind={message.kind} />
                <span
                  className={cn("text-xs font-medium", STATUS_CLASS[message.status])}
                  data-testid="message-status"
                >
                  {t(`mailbox.status.${message.status}`)}
                </span>
              </div>

              {message.reason && message.status !== "imported" && (
                <p className="mt-2 text-sm text-muted-foreground">{reasonText(message.reason)}</p>
              )}

              {files.length > 0 && (
                <ul className="mt-3 space-y-1 border-t pt-3 text-sm" data-testid="message-files">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1"
                      data-testid="message-file"
                    >
                      <span className="max-w-72 truncate">{file.name}</span>
                      <span
                        className={cn("text-xs", OUTCOME_CLASS[file.outcome])}
                        data-outcome={file.outcome}
                      >
                        {t(`mailbox.outcome.${file.outcome}`)}
                        {file.reason ? ` · ${reasonText(file.reason)}` : ""}
                      </span>
                      {file.documentId && (
                        <Link to={`/documents/${file.documentId}`} className="ml-auto">
                          <BelegNumber value={file.docNumber} />
                        </Link>
                      )}
                      {held && !file.documentId && known?.get(file.sha256) && (
                        <Link
                          to={`/documents/${known.get(file.sha256)!.id}`}
                          className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground"
                          data-testid="file-already-filed"
                        >
                          {t("mailbox.alreadyFiled")}
                          <BelegNumber value={known.get(file.sha256)!.docNumber} />
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {held && canWrite && (
                <div className="mt-3 flex flex-wrap gap-2 border-t pt-3">
                  <Button
                    size="sm"
                    onClick={() => void runApprove(message, true)}
                    disabled={approve.isPending}
                    data-testid="approve-and-allow"
                  >
                    <Check /> {t("mailbox.approveAndAllow")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void runApprove(message, false)}
                    disabled={approve.isPending}
                    data-testid="approve-once"
                  >
                    {t("mailbox.approveOnce")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="discard-message"
                    disabled={discard.isPending}
                    onClick={async () => {
                      await discard.mutateAsync(message.id);
                      toast.success(t("mailbox.discarded"));
                    }}
                  >
                    <Trash2 /> {t("mailbox.discard")}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
